/**
 * Box Truck Loads → Driver Verifications. The operator's queue of driver bidding-verification
 * requests (driverProfile.boxTruckInfo). An operator reviews the submitted contact + equipment and
 * flips a driver Active (enables bidding) or Inactive (disables it). Cross-tenant, operator-gated
 * server-side. Bespoke table matching the loadboard's conventions (ds2 tokens, no MUI).
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { ArrowLeft, Building2, IdCard, Search, Truck, UserPlus } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { BOX_TRUCK_ASSET_DOC_LABELS, BOX_TRUCK_OWNER_DOC_LABELS, BoxTruckVerificationStatus } from '@numeo/types-core'
import { Badge, Button, ProgressBar, Spinner } from '@/components/ds2'
import { TmsTruckSeatLimitModal } from '@/features/billing/tms/TmsTruckSeatLimitModal'
import { ClaimDriverModal } from '../components/ClaimDriverModal'
import { useClaimDriverWithTruck } from '../hooks/useClaimDriverWithTruck'
import { useSnackbar } from '@/contexts/SnackbarContext'
import { getUserErrorMessage } from '@/utils/get-user-error-message'
import { SnackbarType } from '@/utils/snackbar'
import { useDebouncedValue } from 'hooks/useDebouncedValue'
import { useBoxTruckVerifications } from '../hooks/useBoxTruckVerifications'
import { CellCopy } from '../components/CellCopy'
import { MyDriverQuickView } from '../components/MyDriverQuickView'
import { DriverHeaderFacts } from '../components/DriverHeaderFacts'
import { DriverDocumentsCompact } from '../components/DriverDocumentsCompact'
import { AssignedDocumentsPanel } from '../components/AssignedDocumentsPanel'
import { DocumentSection } from '../components/DocumentSection'
import { useDriverAssignments } from '../hooks/useDriverAssignments'

import {
    addBoxTruckLeadSource,
    BoxTruckVerification,
    fetchAssignedOwnerDocumentBlobUrl,
    fetchAssignedOwnerDocuments,
    fetchAssignedVehicleDocumentBlobUrl,
    fetchAssignedVehicleDocuments,
    fetchBoxTruckVerificationDocumentBlobUrl,
    fetchBoxTruckVerificationDocuments,
} from '../services/boxTruckLoadsApi'
import { BoxTruckFilterSelect } from '../components/BoxTruckFilterSelect'
import { useBoxTruckLeadSources } from '../hooks/useBoxTruckLeadSources'
import { useRestoredListScroll } from '../hooks/useRestoredListScroll'
import { boxTruckStatusBadge, fmtDateTime, BOX_TRUCK_EQUIPMENT_FILTER_OPTIONS } from '../utils/format'
import { hasResolvableAssignments } from '../utils/rowCapabilities'
import { boxTruckVerificationCompleteness } from '../utils/registration'
import { BoxTruckLastLocationCell } from '../components/BoxTruckLastLocationCell'

const HEADERS = ['Driver', 'Phone / Equipment', 'Submitted', 'Registration', 'Last location', 'Status', 'Drivers']

const STATUS_FILTERS: { label: string; value: BoxTruckVerificationStatus | undefined }[] = [
    { label: 'All', value: undefined },
    { label: 'Unverified', value: 'pending' },
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
]

export const BoxTruckDriverVerificationsPage = () => {
    const navigate = useNavigate()
    const {
        items,
        total,
        loading,
        loadingMore,
        hasMore,
        error,
        statusFilter,
        setStatusFilter,
        equipmentFilter,
        setEquipmentFilter,
        setSearch,
        loadMore,
        refresh,
        claimDriver,
    } =
        // MC-holders live on the Company Owners page; this queue shows no-MC self-submits + legacy/operator rows.
        useBoxTruckVerifications('drivers')
    const { showSnackbar } = useSnackbar()
    // The raw search box value; debounced before it drives a refetch (resets to page 1).
    const [searchInput, setSearchInput] = useState('')
    const debouncedSearch = useDebouncedValue(searchInput, 300)
    useEffect(() => setSearch(debouncedSearch), [debouncedSearch, setSearch])
    // The driver awaiting the "Add My Drivers" confirmation, and whether the invite is in flight.
    // Owned here, not in the modal: "+ Add new source" persists a source and must leave it SELECTED, and
    // that flow lives on this page. Held in the modal it could add an option nobody had chosen.
    const [leadSource, setLeadSource] = useState('')
    // A seat refusal the operator has to answer, and the vehicle it is holding. The driver is ALREADY
    // claimed by the time this can appear, so a confirmed overage re-sends only the truck — never the
    // claim, which is the one step that cannot be repeated.
    // Lead source picked in the Add-driver modal (optional). The "+ Add new" input persists a new source (via
    // the hook's setter) before it's selectable; the built-in defaults are always shown.
    const [showAddSource, setShowAddSource] = useState(false)
    const [newSourceInput, setNewSourceInput] = useState('')
    const [addingSource, setAddingSource] = useState(false)
    const { sources: allLeadSources, setCustom: setCustomSources } = useBoxTruckLeadSources()

    /** Reset the lead-source picker (called whenever the claim modal closes). */
    const resetLeadSourcePicker = () => {
        setLeadSource('')
        setShowAddSource(false)
        setNewSourceInput('')
    }
    const {
        state: { claimTarget, claiming, seatPrompt },
        actions: { startClaim, confirmClaim, confirmSeatOverage, dismissSeatPrompt, closeClaimModal },
    } = useClaimDriverWithTruck({ claimDriver, showSnackbar, onFinished: resetLeadSourcePicker })

    /** Persist a typed custom source, then select it. */
    const addCustomSource = async () => {
        const value = newSourceInput.trim()
        if (!value) return
        setAddingSource(true)
        try {
            const list = await addBoxTruckLeadSource(value)
            setCustomSources(list)
            setLeadSource(value)
            setNewSourceInput('')
            setShowAddSource(false)
        } catch (e) {
            showSnackbar(SnackbarType.ERROR, getUserErrorMessage(e) || 'Couldn’t add the source. Try again.')
        } finally {
            setAddingSource(false)
        }
    }
    // The driver whose detail (read-only documents + registration completeness) is open, if any.
    const [selected, setSelected] = useState<BoxTruckVerification | null>(null)
    // Opening a driver replaces the queue with that driver's detail, so the list keeps its place instead of
    // snapping back to the top every time the operator checks a row.
    const listRef = useRef<HTMLDivElement | null>(null)
    const { onScroll: rememberScroll, reset: resetScroll } = useRestoredListScroll(listRef, !!selected)
    // Infinite scroll: load the next page when the operator nears the bottom of the list.
    const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
        rememberScroll(e)
        const el = e.currentTarget
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 240) loadMore()
    }
    // A new search or filter RE-LISTS the queue from page 1, which starts at the top — so drop the remembered
    // offset rather than restoring a deep scroll onto a short filtered list.
    useEffect(() => resetScroll(), [debouncedSearch, statusFilter, equipmentFilter, resetScroll])

    // ONE claim modal for both entry points: the queue row and the driver's own detail page, which used to
    // claim with no confirmation at all — and so never had anywhere to ask about the truck.
    const claimModal = claimTarget ? (
        <ClaimDriverModal
            driver={claimTarget}
            leadSources={allLeadSources}
            leadSource={leadSource}
            onLeadSourceChange={setLeadSource}
            submitting={claiming}
            onClose={closeClaimModal}
            onSubmit={(values) => void confirmClaim(values)}
            sourceExtra={
                showAddSource ? (
                    <div className="mt-2 flex gap-2">
                        <input
                            autoFocus
                            value={newSourceInput}
                            onChange={(e) => setNewSourceInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && newSourceInput.trim() && void addCustomSource()}
                            placeholder="e.g. TikTok"
                            maxLength={60}
                            className="h-9 flex-1 rounded-xl bg-surface px-3 text-sm text-foreground shadow-field outline-none focus:ring-2 focus:ring-accent"
                        />
                        <Button variant="outline" size="sm" loading={addingSource} disabled={!newSourceInput.trim()} onClick={() => void addCustomSource()}>
                            Add
                        </Button>
                    </div>
                ) : (
                    <button type="button" onClick={() => setShowAddSource(true)} className="mt-2 text-xs font-medium text-accent hover:underline">
                        + Add new source
                    </button>
                )
            }
        />
    ) : null

    // Rendered alongside the claim modal in BOTH branches: a seat refusal can come from the queue row or
    // from the driver's own detail page, and a dialog that only exists on one of them is a dead end.
    const seatModal = (
        <TmsTruckSeatLimitModal
            prompt={seatPrompt}
            loading={claiming}
            onClose={dismissSeatPrompt}
            onConfirm={() => void confirmSeatOverage()}
            onUpgrade={() => navigate('/billing?tab=tms')}
        />
    )

    // Detail page — the same read-first QUICK VIEW as My Drivers (copyable fields + per-type document chips
    // opening an in-place lightbox), served through the queue-scoped review endpoints. Activate/Deactivate
    // lives only in My Drivers.
    if (selected) {
        return (
            <>
                {/* Claiming from here used to be a bare button: one click, driver added, no vehicle asked for
                    and nothing said about it. It goes through the same modal as the queue row now. */}
                <VerificationDetail driver={selected} onBack={() => setSelected(null)} onRequestClaim={() => startClaim(selected)} claiming={claiming} />
                {claimModal}
                {seatModal}
            </>
        )
    }

    return (
        <div className="flex h-full flex-col bg-background text-foreground">
            {/* Toolbar — atrek-style: everything left-aligned, no page title (the sidebar names the page). */}
            <div className="flex items-center gap-2.5 border-b border-border px-3 py-1.5">
                <span className="flex h-10 items-center whitespace-nowrap rounded-xl border border-border bg-surface px-4 text-sm font-semibold tabular-nums text-foreground">
                    Total: {loading ? '…' : total.toLocaleString()}
                </span>
                <div className="relative">
                    <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-muted" strokeWidth={1.75} />
                    <input
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder="Search driver (name, email, phone)"
                        className="h-10 w-80 rounded-xl bg-surface pl-9 pr-3 text-sm text-foreground shadow-field outline-none focus:ring-2 focus:ring-accent"
                    />
                </div>
                <BoxTruckFilterSelect
                    value={statusFilter ?? ''}
                    onChange={(v) => setStatusFilter((v || undefined) as BoxTruckVerificationStatus | undefined)}
                    className="w-36"
                    options={STATUS_FILTERS.map((f) => ({ label: f.value ? f.label : 'Status: All', value: f.value ?? '' }))}
                />
                <BoxTruckFilterSelect
                    value={equipmentFilter}
                    onChange={setEquipmentFilter}
                    className="w-44"
                    options={[{ label: 'Equipment: All', value: '' }, ...BOX_TRUCK_EQUIPMENT_FILTER_OPTIONS.map((label) => ({ label, value: label }))]}
                />
            </div>

            <div ref={listRef} className="flex-1 overflow-y-auto" onScroll={onScroll}>
                {error ? (
                    <div className="flex flex-col items-center justify-center py-24 text-center">
                        <p className="text-sm font-medium text-danger">Couldn’t load verifications</p>
                        <p className="mt-1 max-w-sm text-xs text-foreground-muted">{error}</p>
                        <Button variant="outline" size="sm" onClick={refresh} className="mt-4">
                            Retry
                        </Button>
                    </div>
                ) : loading && items.length === 0 ? (
                    <div className="flex flex-1 items-center justify-center py-24">
                        <Spinner color="accent" size={24} />
                        <span className="ml-2 text-sm text-foreground-muted">Loading verifications…</span>
                    </div>
                ) : items.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-24 text-center">
                        <p className="text-sm font-medium text-foreground">No verification requests</p>
                        <p className="mt-1 text-xs text-foreground-muted">Requests from drivers will appear here as they submit them.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[1160px] border-collapse text-[13px] leading-tight">
                            <thead>
                                <tr className="border-b border-border">
                                    {HEADERS.map((h) => (
                                        <th
                                            key={h}
                                            className="whitespace-nowrap px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-foreground-muted"
                                        >
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((v) => {
                                    const badge = boxTruckStatusBadge(v.boxTruckInfo.status)
                                    const regPct = boxTruckVerificationCompleteness(v.boxTruckInfo.info, v.boxTruckInfo.verificationPath)
                                    return (
                                        <tr key={v.userId} className="cursor-pointer border-b border-border/60 hover:bg-surface" onClick={() => setSelected(v)}>
                                            <td className="px-3 py-1.5">
                                                <div className="flex items-center gap-1 text-[15px] font-bold text-foreground">
                                                    <span className="truncate">{v.boxTruckInfo.info?.name || v.name || '—'}</span>
                                                    {(v.boxTruckInfo.info?.name || v.name) && (
                                                        <CellCopy value={v.boxTruckInfo.info?.name || v.name || ''} label="name" />
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-1 text-xs text-foreground-muted">
                                                    <span className="truncate">{v.email}</span>
                                                    {v.email && <CellCopy value={v.email} label="email" />}
                                                </div>
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-1.5">
                                                <div className="flex items-center gap-1 text-foreground-muted">
                                                    <span>{v.boxTruckInfo.info?.phone || '—'}</span>
                                                    {v.boxTruckInfo.info?.phone && <CellCopy value={v.boxTruckInfo.info.phone} label="phone" />}
                                                </div>
                                                <div className="text-xs text-foreground-muted">
                                                    {v.boxTruckInfo.info?.equipmentType || '—'}
                                                    {v.boxTruckInfo.info?.maxCargoWeightLbs != null
                                                        ? ` · ${v.boxTruckInfo.info.maxCargoWeightLbs.toLocaleString()} lbs`
                                                        : ''}
                                                </div>
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-1.5 text-foreground-muted">{fmtDateTime(v.boxTruckInfo.submittedAt)}</td>
                                            <td className="whitespace-nowrap px-3 py-1.5" title={`${regPct}% of registration filled`}>
                                                <div className="w-20">
                                                    <ProgressBar size="sm" value={regPct} color={regPct === 100 ? 'success' : 'accent'} />
                                                </div>
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-1.5 text-foreground-muted">
                                                <BoxTruckLastLocationCell lastLocation={v.lastLocation} profileLocation={v.profileLocation} />
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-1.5">
                                                <Badge variant="soft" type={badge.type} label={badge.label} />
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-1.5" onClick={(e) => e.stopPropagation()}>
                                                {v.claimStatus === 'added' ? (
                                                    <Badge variant="soft" type="success" label="Added" />
                                                ) : (
                                                    <Button variant="outline" size="sm" prefix={<UserPlus size={14} />} onClick={() => startClaim(v)}>
                                                        Add to Drivers
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
                {loadingMore && (
                    <div className="flex items-center justify-center gap-2 py-4 text-xs text-foreground-muted">
                        <Spinner color="accent" size={16} /> Loading more…
                    </div>
                )}
                {!loading && items.length > 0 && (
                    <div className="px-6 py-3 text-center text-[11px] text-foreground-muted">
                        Showing {items.length} of {total}
                        {hasMore ? '' : ' · all drivers loaded'}
                    </div>
                )}
            </div>

            {claimModal}
            {seatModal}
        </div>
    )
}

/** Verification review detail: quick-view fields + the driver's self-submitted documents (grouped by type,
 *  in-place lightbox), with the registration completeness in the header. Read-only — claiming/activation
 *  live on the queue row / My Drivers. */
const VerificationDetail = ({
    driver,
    onBack,
    onRequestClaim,
    claiming,
}: {
    driver: BoxTruckVerification
    onBack: () => void
    onRequestClaim: () => void
    claiming: boolean
}) => {
    const badge = boxTruckStatusBadge(driver.boxTruckInfo.status)
    const navigate = useNavigate()
    // Adding, its outcome and the jump to My Drivers are all owned by the page's one claim handler now.

    const [docs, setDocs] = useState<import('@numeo/types-core').BoxTruckDocument[] | null>(null)
    useEffect(() => {
        let cancelled = false
        setDocs(null)
        fetchBoxTruckVerificationDocuments(driver.userId)
            .then((rows) => !cancelled && setDocs(rows))
            .catch(() => !cancelled && setDocs([]))
        return () => {
            cancelled = true
        }
    }, [driver.userId])

    // Only for a driver this operator has added: assignments resolve from the caller's claim, so for an
    // unclaimed queue row the answer is empty by construction and the request is not worth making. Keyed by
    // driver inside the hook, so a late answer for the previous row cannot paint over this one.
    const added = hasResolvableAssignments(driver)
    const assignments = useDriverAssignments(driver.userId, added)
    // No vehicle holds this driver's paperwork yet — not added here, or added without one. The section then
    // shows the vehicle documents the driver handed in, which is what a reviewer needs before adding them.
    const noVehicle = !added || (assignments.data !== null && !assignments.data.vehicle)
    // Completeness over the freshly-fetched documents merged onto the row's info (a queue row may carry a
    // stale/absent documents array).
    const regPct = useMemo(
        () =>
            boxTruckVerificationCompleteness(
                { ...driver.boxTruckInfo.info, documents: docs ?? driver.boxTruckInfo.info?.documents },
                driver.boxTruckInfo.verificationPath
            ),
        [driver, docs]
    )
    return (
        <div className="flex h-full flex-col bg-background text-foreground">
            <div className="flex flex-wrap items-center gap-3 border-b border-border px-6 py-3">
                <Button variant="ghost" size="sm" iconOnly onClick={onBack} aria-label="Back to verifications">
                    <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
                </Button>
                <div className="min-w-0 flex-1">
                    <h1 className="truncate text-base font-semibold">{driver.boxTruckInfo.info?.name || driver.name || driver.email}</h1>
                    <div className="truncate text-xs text-foreground-muted">{driver.email}</div>
                </div>
                {/* Read-only here, and deliberately: this queue's rows never offered a location correction
                    either, and a reviewer deciding whether to take a driver on is not the person whose
                    reading of where that driver is should overwrite the driver's own. */}
                <DriverHeaderFacts driver={driver} />
                {driver.claimStatus === 'added' ? (
                    <Button variant="outline" size="md" onClick={() => navigate('/profiles/drivers')}>
                        Open in Drivers
                    </Button>
                ) : (
                    <Button variant="primary" size="md" prefix={<UserPlus size={15} />} loading={claiming} onClick={onRequestClaim}>
                        Add to Drivers
                    </Button>
                )}
                <div className="flex items-center gap-2 rounded-xl border border-border bg-surface px-3 py-1.5" title="Registration completeness">
                    <div className="w-20">
                        <ProgressBar size="sm" value={regPct} color={regPct === 100 ? 'success' : 'accent'} />
                    </div>
                    <span className="text-xs font-semibold tabular-nums text-foreground">{regPct}%</span>
                </div>
                <Badge variant="soft" type={badge.type} label={badge.label} />
            </div>
            <div className="flex-1 overflow-y-auto p-6">
                <div className="flex min-h-full flex-col space-y-4">
                    <MyDriverQuickView
                        driver={driver}
                        assignments={assignments.data}
                        assignmentsFailed={assignments.failed}
                        onRetryAssignments={assignments.reload}
                    />
                    {/* Same full-width documents card as My Drivers, section for section. This queue is where
                        an operator decides whether to take a driver on, so its paperwork is read the same way
                        it will be read afterwards — and one shape means a change to either surface cannot
                        leave the other behind. */}
                    <div className="flex-1 space-y-4 rounded-2xl bg-surface p-5 shadow-surface">
                        <DocumentSection icon={<IdCard size={16} />} title="Driver documents" hint="Handed in by this driver">
                            {docs === null ? (
                                <div className="flex items-center gap-2 py-4 text-xs text-foreground-muted">
                                    <Spinner color="accent" size={16} /> Loading documents…
                                </div>
                            ) : (
                                <DriverDocumentsCompact driverId={driver.userId} docs={docs} fetchBlobUrl={fetchBoxTruckVerificationDocumentBlobUrl} />
                            )}
                        </DocumentSection>
                        <div className="h-px bg-border" />
                        <AssignedDocumentsPanel
                            // Keyed by driver — see the same panels on My Drivers.
                            key={`owner-${driver.userId}`}
                            icon={<Building2 size={16} />}
                            scope="owner"
                            title="Company documents"
                            hint="The owner this driver is attached to"
                            actionLabel="See company documents"
                            labels={BOX_TRUCK_OWNER_DOC_LABELS}
                            load={() => fetchAssignedOwnerDocuments(driver.userId)}
                            fetchBlobUrl={(documentId) => fetchAssignedOwnerDocumentBlobUrl(driver.userId, documentId)}
                            emptyText={added ? 'No documents on this owner yet.' : 'Not attached to an owner yet.'}
                            unavailableText={assignments.data && !assignments.data.owner ? 'This driver is not attached to an owner.' : undefined}
                        />
                        <div className="h-px bg-border" />
                        <AssignedDocumentsPanel
                            key={`vehicle-${driver.userId}`}
                            icon={<Truck size={16} />}
                            scope="vehicle"
                            title="Vehicle documents"
                            hint={noVehicle ? 'Handed in by this driver — no vehicle is assigned yet' : 'The asset this driver is seated on'}
                            actionLabel="See vehicle documents"
                            labels={BOX_TRUCK_ASSET_DOC_LABELS}
                            load={() => fetchAssignedVehicleDocuments(driver.userId)}
                            fetchBlobUrl={(documentId) => fetchAssignedVehicleDocumentBlobUrl(driver.userId, documentId)}
                            emptyText={noVehicle ? 'This driver has not sent any vehicle documents.' : 'No documents on this vehicle yet.'}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}
